import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Attachments from 'components/customer/conversation_history/conversation_items_v2/attachments/attachments';
import { HeightChangeContext } from 'components/customer/conversation_history/virtualized/list_item';
import connect from 'components/lib/connect';
import htmlToText from 'models/conversation_item/html_to_text';
import {
  emailHeaders,
  HtmlContentModalContainer,
} from 'components/customer/conversation_history/conversation_items_v2/email/view_original_html_modal';
import ProfileAvatar from 'components/lib/profile_avatar';
import SafeHtml from 'components/common/safe_html';
import TaskEmail from 'models/task_email';

// The panel has room to read a reply in place; the feed card is a summary that opens the panel.
const PANEL_CLAMP_LINES = 12;
const FEED_CLAMP_LINES = 5;

export function TaskThreadEmail({ className, email, inItemDisplay, senderName }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const bodyRef = useRef(null);
  const inbound = email.isInbound();
  // Same policy as the conversation feed: our own outbound HTML renders rich, externally-authored
  // mail renders as text with the original behind a sandboxed modal.
  const renderRich = !inbound && !!email.bodyHtml;
  const isClipped = useIsClipped(bodyRef, renderRich ? email.bodyHtml : email.bodyPlain);
  useManualHeightChange(isExpanded);

  // Vendors' clients sometimes send HTML with no text part; fall back so the card is never blank.
  const bodyText = email.bodyPlain || htmlToText(email.bodyHtml || '');

  return (
    <StyledTaskEmail className={className} data-aid="taskThreadEmail">
      <StyledTaskEmailHeader>
        <StyledTaskEmailDirection>
          {inbound && <StyledAvatar noWrapper profile={{ name: senderName }} />}
          <StyledTaskEmailDirectionLabel>
            {inbound ? t('Email Received') : t('Email Sent')}
          </StyledTaskEmailDirectionLabel>
        </StyledTaskEmailDirection>
        <StyledTaskEmailSender data-aid="taskThreadEmail-sender">{senderName}</StyledTaskEmailSender>
      </StyledTaskEmailHeader>
      <StyledTaskEmailField>
        <StyledTaskEmailLabel>{inbound ? t('From:') : t('To:')}</StyledTaskEmailLabel>{' '}
        {inbound ? email.from : (email.to || []).join(', ')}
      </StyledTaskEmailField>
      {renderRecipients('cc', t('Cc:'))}
      {renderRecipients('bcc', t('Bcc:'))}
      <StyledTaskEmailField>
        <StyledTaskEmailLabel>{t('Subject:')}</StyledTaskEmailLabel> {email.subject}
      </StyledTaskEmailField>
      {renderBody()}
      {renderBodyActions()}
      {renderAttachments()}
      {showOriginal && (
        <HtmlContentModalContainer
          attachments={email.attachments}
          headers={emailHeaders(email, email.timestamp)}
          html={email.bodyHtml}
          onClose={() => setShowOriginal(false)}
        />
      )}
    </StyledTaskEmail>
  );

  function renderBody() {
    const clamp = !isExpanded && (inItemDisplay ? FEED_CLAMP_LINES : PANEL_CLAMP_LINES);

    if (renderRich) {
      return (
        <StyledTaskEmailRichBody $clamp={clamp} data-aid="taskThreadEmail-body" ref={bodyRef}>
          <SafeHtml html={email.bodyHtml} setDirection />
        </StyledTaskEmailRichBody>
      );
    }

    return (
      <StyledTaskEmailPlainBody $clamp={clamp} data-aid="taskThreadEmail-body" ref={bodyRef}>
        {bodyText}
      </StyledTaskEmailPlainBody>
    );
  }

  function renderBodyActions() {
    const canToggle = isClipped || isExpanded;
    if (!canToggle && !email.bodyHtml) return null;

    return (
      <StyledActions>
        {canToggle && (
          <StyledLinkButton data-aid="taskThreadEmail-toggleBody" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? t('Show less') : t('Show more')}
          </StyledLinkButton>
        )}
        {email.bodyHtml && (
          <StyledLinkButton data-aid="taskThreadEmail-viewOriginal" onClick={() => setShowOriginal(true)}>
            {t('View original email')}
          </StyledLinkButton>
        )}
      </StyledActions>
    );
  }

  function renderRecipients(attr, label) {
    const addresses = email[attr];
    if (!addresses || !addresses.length) return null;

    return (
      <StyledTaskEmailField data-aid={`taskThreadEmail-${attr}`}>
        <StyledTaskEmailLabel>{label}</StyledTaskEmailLabel> {addresses.join(', ')}
      </StyledTaskEmailField>
    );
  }

  // Inline images are part of a rich body; when the body is text they have nowhere else to appear.
  function renderAttachments() {
    const attachments = renderRich ? email.regularAttachments() : email.attachments;
    if (!attachments.length) return null;

    return <StyledAttachments attachments={attachments} itemId={email.id} pathPrefix="email" />;
  }
}

TaskThreadEmail.propTypes = {
  className: PropTypes.string,
  email: PropTypes.instanceOf(TaskEmail).isRequired,
  inItemDisplay: PropTypes.bool,
  senderName: PropTypes.string,
};

// An agent expanding or collapsing the body is a manual height change: the feed should let the card
// grow downward rather than shift the scroll position to compensate, as it does for content that
// changes on its own. The first measurement after mount is not manual.
function useManualHeightChange(isExpanded) {
  const { onHeightChange } = useContext(HeightChangeContext);
  // The callback's identity changes with every measured height, so it is read through a ref: only
  // the toggle itself may fire this.
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onHeightChangeRef.current && onHeightChangeRef.current({ isManual: true });
  }, [isExpanded]);
}

// The clamp is CSS, so whether it actually truncated is only knowable after layout, and a rich body
// grows again as its images load.
function useIsClipped(ref, dependency) {
  const [isClipped, setIsClipped] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) {
      setIsClipped(false);
      return undefined;
    }

    const measure = () => setIsClipped(node.scrollHeight > node.clientHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    Array.from(node.querySelectorAll('img')).forEach(img => observer.observe(img));
    return () => observer.disconnect();
  }, [ref, dependency]);

  return isClipped;
}

function mapStateToProps({ getProvider }, { email }) {
  if (email.isInbound()) {
    return { senderName: email.fromName || email.from };
  }

  const agent = email.agentId ? getProvider('agents').findBy({ id: email.agentId }) : null;
  return { senderName: agent ? agent.getDisplayName() : email.fromName || email.from };
}

const TaskThreadEmailCard = connect(mapStateToProps)(TaskThreadEmail);
export default TaskThreadEmailCard;

const StyledTaskEmail = styled.div`
  background-color: ${p => p.theme.colors.gray100};
  border: 1px solid ${p => p.theme.colors.gray200};
  border-radius: 8px;
  padding: 12px;
`;

const StyledTaskEmailHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const StyledTaskEmailDirection = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const StyledTaskEmailDirectionLabel = styled.span`
  font-weight: bold;
`;

const StyledTaskEmailSender = styled.span`
  color: ${p => p.theme.colors.gray600};
  min-width: 0;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledAvatar = styled(ProfileAvatar)`
  flex: none;
`;

const StyledTaskEmailField = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  margin-top: 8px;
  overflow-wrap: break-word;
`;

const StyledTaskEmailLabel = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-weight: bold;
`;

const StyledAttachments = styled(Attachments)`
  margin-top: 8px;
`;

const StyledTaskEmailPlainBody = styled.div`
  margin-top: 8px;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  ${p =>
    p.$clamp &&
    `-webkit-box-orient: vertical; -webkit-line-clamp: ${p.$clamp}; display: -webkit-box; overflow: hidden;`}
`;

const StyledTaskEmailRichBody = styled.div`
  margin-top: 8px;
  overflow-wrap: break-word;
  ${p => p.$clamp && `max-height: ${p.$clamp * 1.5}em; overflow: hidden;`}

  img {
    max-width: 100%;
  }
`;

const StyledActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const StyledLinkButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.green400};
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;
